import React from "react";
import {
  Box,
  Paper,
  Typography,
  Rating,
  Avatar,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import { ReviewViewModel, ReviewStatus } from "../../configuration/api/apiSchema";
import { formatDistanceToNow } from "date-fns";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },
}));

const TagsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

interface ReviewItemProps {
  review: ReviewViewModel;
  onRespond?: (reviewId: string) => void;
  showActions?: boolean;
}

const ReviewItem: React.FC<ReviewItemProps> = ({
  review,
  onRespond,
  showActions = false,
}) => {
  const getStatusColor = (status?: ReviewStatus) => {
    switch (status) {
      case ReviewStatus.Pending:
        return "#ff9800"; // orange
      case ReviewStatus.Submitted:
        return "#4caf50"; // green
      case ReviewStatus.Expired:
        return "#f44336"; // red
      default:
        return "#757575"; // grey
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <StyledPaper elevation={1}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box display="flex" alignItems="center">
          <Avatar
            src={review.reviewer?.avatarUrl || undefined}
            alt={`${review.reviewer?.firstName || "User"}`}
            sx={{ width: 50, height: 50, mr: 2 }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {review.reviewer?.firstName} {review.reviewer?.lastName}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5}>
              <Rating value={review.rating || 0} readOnly precision={0.5} size="small" />
              <Typography variant="body2" color="text.secondary" ml={1}>
                {review.rating}/5
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box textAlign="right">
          <Chip
            label={review.status || "Unknown"}
            size="small"
            sx={{
              bgcolor: getStatusColor(review.status),
              color: "white",
              fontWeight: "medium",
            }}
          />
          <Typography variant="caption" display="block" mt={0.5}>
            {formatDate(review.createdAt)}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body1" mt={2} sx={{ whiteSpace: "pre-wrap" }}>
        {review.comment || "No comment provided."}
      </Typography>

      {review.tags && review.tags.length > 0 && (
        <TagsContainer>
          {review.tags.map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
        </TagsContainer>
      )}

      {review.response && (
        <Box mt={2} mb={2}>
          <Divider />
          <Box mt={2} bgcolor="#f5f5f5" p={2} borderRadius={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Response:
            </Typography>
            <Typography variant="body2">{review.response}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {formatDate(review.responseDate)}
            </Typography>
          </Box>
        </Box>
      )}

      {showActions && review.status === ReviewStatus.Submitted && !review.response && onRespond && (
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => review.id && onRespond(review.id)}
          >
            Respond to Review
          </Button>
        </Box>
      )}
    </StyledPaper>
  );
};

export default ReviewItem; 